import { FC, useRef, useState } from "react";
import { SelectFormat, SelectSpecialtyFilter } from "./filters";
import { AdequacyFilters } from "@app/projects/network-assessment/adequacy";
import { ReportSettingsProps } from "./report-settings/reportSettings";
import { ReportPanelBodyBaseProps } from "./reportPanelBodyBase";
import { ExportZipAnalysisRequestModel, } from "../exportModels";
import { ExportFormatType, ExportGapFilterType } from "../enumerations";
import GenerateBody from "./generate-body/GenerateBody";
import { reportName as getDefaultReportName } from "./exportPanelBodyHelpers";
import { NetworkAssessmentAnalysisType } from "@app/projects/enumerators";
import { ZipClassAnalysisSearchParameters, ZipCodeAnalysisSearchParameters } from "@app/projects/zip-projects/interfaces"
import * as toast from "./toast";
import ExportService from "../exportService";


interface SpecialtySummaryByZipCodePanelBodyProps extends ReportPanelBodyBaseProps, ReportSettingsProps {
  adequacyFilters: AdequacyFilters;
  projectId: number;
  runConfigurationId: number;
  showExpandableSpecialtiesOption: boolean;
  specialtyGroupCodes: string[];
  reportType: "zipCode" | "zipClass";
  searchParameters: ZipCodeAnalysisSearchParameters | ZipClassAnalysisSearchParameters;
}

const initialSpecialtyFilter = {
  filterType: ExportGapFilterType.All,
  includeExpandableSpecialties: false,
  includeFacilitySpecialties: true,
  includePhysicianSpecialties: true,
};

const getBaseReportName = (reportType:"zipCode"|"zipClass", comparedRunConfigurationId?: number): string => {
  if (!!comparedRunConfigurationId) 
    return reportType === "zipClass" ? "Summary by Classification Comparison" : "Summary by Zip Code Comparison "
  else
    return reportType === "zipClass" ? "Summary by Classification" : "Summary by Zip Code"
}

export const SpecialtySummaryByZipCodePanelBody: FC<SpecialtySummaryByZipCodePanelBodyProps> = (props: SpecialtySummaryByZipCodePanelBodyProps) => {
  const [specialtyFilter, setSpecialtyFilter] = useState(initialSpecialtyFilter);
  const [outputFormat, setOutputFormat] = useState(ExportFormatType.Excel);
  const [emailOnSuccess, setEmailOnSuccess] = useState(true);
  const [reportName, setReportName] = useState(getDefaultReportName(getBaseReportName(props.reportType, props.comparedRunConfigurationId)));
  const [sharedUsers, setSharedUsers] = useState<number[]>([]);
  const exportService = useRef(new ExportService()).current;

  const handleSpecialtyIncludeChange = (selected: boolean, isPhysician: boolean) => {
    const newSpecialtyFilter = { ...specialtyFilter };

    if (isPhysician) {
      newSpecialtyFilter.includePhysicianSpecialties = selected;
    } else {
      newSpecialtyFilter.includeFacilitySpecialties = selected;
    }

    setSpecialtyFilter(newSpecialtyFilter);
  };

  const handleExpandableSpecialtiesIncludedChange = (selected: boolean) => {
    const newSpecialtyFilter = {
      ...specialtyFilter,
      includeExpandableSpecialties: selected,
    };
    setSpecialtyFilter(newSpecialtyFilter);
  }

  const handleSpecialtyGapFilterChange = (filterType: ExportGapFilterType) => {
    const newSpecialtyFilter = { ...specialtyFilter };
    newSpecialtyFilter.filterType = filterType;
    setSpecialtyFilter(newSpecialtyFilter);
  }

  const handleOutputFormatChange = (exportFormat: ExportFormatType) => {
    setOutputFormat(exportFormat);
  }

  const handleGenerateClick = async () => {
    await generateExportAdequacySummaryModel();

    toast.toastInfo(reportName);
  }

  const generateExportAdequacySummaryModel = async () => {
    const summariesModel: ExportZipAnalysisRequestModel = {
      comparedProjectRunConfigurationId: props.comparedRunConfigurationId,
      eMailOnSuccess: emailOnSuccess,
      exportName: reportName,
      formatType: outputFormat,
      includeExpandableSpecialties: specialtyFilter.includeExpandableSpecialties,
      includeFacilitySpecialties: specialtyFilter.includeFacilitySpecialties,
      includePhysicianSpecialties: specialtyFilter.includePhysicianSpecialties,
      networkAssessmentAnalysisType: NetworkAssessmentAnalysisType.ServiceArea,
      regionSetGapFilter: ExportGapFilterType.All,
      regionSetIds: props.regionSetIds,
      runAsynchronous: true,
      searchParametersModel: props.searchParameters,
      sharedUsers: sharedUsers,
      specialtyGapFilter: specialtyFilter.filterType,
      specialtyGroupCodes: props.specialtyGroupCodes,
    };

    if (props.reportType === "zipCode") {
      await exportService.exportZipAnalysisResults(props.projectId, props.runConfigurationId, summariesModel);
    } else if (props.reportType === "zipClass") {
      await exportService.exportZipClassAggregateResults(props.projectId, props.runConfigurationId, summariesModel);
    }
  }

  return (
    <>
      <div className="row">
        <div className="col-3">
          <SelectSpecialtyFilter
            adequacyAnalysisType="zip"
            disabled={props.disableSpecialtyGroupFilters}
            onExpandableSpecialtyIncludeChange={handleExpandableSpecialtiesIncludedChange}
            onSpecialtyGapFilterChange={handleSpecialtyGapFilterChange}
            onSpecialtyIncludeChange={handleSpecialtyIncludeChange}
            showExpandableSpecialtyOption={props.showExpandableSpecialtiesOption}
            specialtyFilter={specialtyFilter}
            testIdSuffix={"specialty-summary-by-zip-code-panel"}
          />
        </div>

        <div className="col-3">
          <SelectFormat
            outputFormat={outputFormat}
            onOutputFormatChange={handleOutputFormatChange}
            excelDisabled={false}
            exportType="specialty-summary-by-zip-code"
          />
        </div>
      </div>

      <GenerateBody
        {...props}
        emailOnSuccess={emailOnSuccess}
        reportName={reportName}
        onGenerateClick={handleGenerateClick}
        onEmailOnSuccessChange={(updateEmail: boolean) => setEmailOnSuccess(updateEmail)}
        onReportNameChange={(newName: string) => setReportName(newName)}
        isLoading={false}
        isValid={reportName !== "" && (specialtyFilter.includeFacilitySpecialties || specialtyFilter.includePhysicianSpecialties)}
        showSharedUsers={props.match.params.module === "adequacy"}
        sharedUsers={sharedUsers}
        updateSelectedSharedUsers={(newSharedUsers: number[]) => setSharedUsers(newSharedUsers)}
        exportType="ZipAnalysisResultsReport"
        projectId={props.projectId}
        networkAssessmentAnalysisType={NetworkAssessmentAnalysisType.ServiceArea}
      />
    </>
  );
}